/* Small display helpers, kept pure so they are unit-tested without a browser. */

export const spaceKindLabel = (kind: string): string =>
  ({
    meeting_room: 'Meeting room',
    desk: 'Desk',
    workspace_area: 'Workspace area',
    parking_spot: 'Parking spot',
    lobby: 'Lobby',
    point_of_interest: 'Point of interest',
    unit: 'Unit',
  })[kind] ?? kind;

export function problemMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const problem = error as { code?: string; message: string };
    return problem.code ? `${problem.message} (${problem.code})` : problem.message;
  }
  return 'Something went wrong';
}

/** Suggests the next free code: B0001-F01-D03 after B0001-F01-D02. */
export function nextCode(existing: string[], prefix: string): string {
  const numbers = existing
    .filter((code) => code.startsWith(prefix))
    .map((code) => Number(code.slice(prefix.length)))
    .filter(Number.isFinite);
  const next = (numbers.length > 0 ? Math.max(...numbers) : 0) + 1;
  return `${prefix}${String(next).padStart(2, '0')}`;
}

export const bookingStatusColor = (status: string): string =>
  ({ approved: 'green', pending: 'yellow', cancelled: 'gray', sync_error: 'red' })[status] ?? 'gray';

export const bookingTypeLabel = (type: string): string =>
  ({ space: 'Room', desk: 'Desk', event: 'Event', parking: 'Parking' })[type] ?? type;

/** A time range in the building's time zone, e.g. "Tue 3 Nov, 09:00–10:30". */
export function timeRange(start: string, end: string, timeZone: string): string {
  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const time = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit' });
  const from = new Date(start);
  const to = new Date(end);
  const sameDay = day.format(from) === day.format(to);
  return sameDay
    ? `${day.format(from)}, ${time.format(from)}–${time.format(to)}`
    : `${day.format(from)} ${time.format(from)} – ${day.format(to)} ${time.format(to)}`;
}

export function relativeTime(at: string | null, now: Date = new Date()): string {
  if (!at) return 'never';
  const minutes = Math.round((now.getTime() - new Date(at).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export interface ListedUser {
  id: string;
  email: string | null;
  displayName: string | null;
  role: 'admin' | 'member';
  isOperator: boolean;
  status: 'invited' | 'active' | 'deactivated';
}

/** The chips on the Users screen, as in the Figma Users frame (All, Admin, User, Global admin). */
export const USER_FILTERS = ['all', 'admin', 'member', 'operator', 'invited', 'deactivated'] as const;
export type UserFilter = (typeof USER_FILTERS)[number];

export const userFilterLabel = (filter: UserFilter): string =>
  ({
    all: 'All',
    admin: 'Admin',
    member: 'User',
    operator: 'Global admin',
    invited: 'Invited',
    deactivated: 'Deactivated',
  })[filter];

export function filterUsers<T extends ListedUser>(users: T[], filter: UserFilter, query: string): T[] {
  const needle = query.trim().toLowerCase();
  return users.filter((user) => {
    const matches =
      needle === '' ||
      (user.displayName ?? '').toLowerCase().includes(needle) ||
      (user.email ?? '').toLowerCase().includes(needle);
    if (!matches) return false;
    switch (filter) {
      case 'all':
        return true;
      case 'operator':
        return user.isOperator;
      case 'invited':
      case 'deactivated':
        return user.status === filter;
      default:
        return user.role === filter && user.status !== 'deactivated';
    }
  });
}

export const initials = (name: string | null, email: string | null): string =>
  (name ?? email ?? '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

/** The names the Figma Users screen uses: Admin, User, Global admin. */
export function roleLabel(user: { role: 'admin' | 'member'; isOperator: boolean }): string {
  if (user.isOperator) return 'Global admin';
  return user.role === 'admin' ? 'Admin' : 'User';
}

/** The next free building code: B0003 after B0001 and B0002. */
export function nextBuildingCode(existing: string[]): string {
  const numbers = existing.map((code) => Number(code.slice(1))).filter(Number.isFinite);
  return `B${String((numbers.length > 0 ? Math.max(...numbers) : 0) + 1).padStart(4, '0')}`;
}

/** The UTC offset of a time zone on a day, e.g. "+01:00" for Prague in November. */
export function zoneOffset(day: string, timeZone: string): string {
  const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
    .formatToParts(new Date(`${day}T12:00:00Z`))
    .find((part) => part.type === 'timeZoneName')?.value;
  const offset = name?.replace('GMT', '') ?? '';
  return offset === '' ? '+00:00' : offset;
}

/** One calendar day in a building's time zone as the API's from/to instants. */
export function dayWindow(day: string, timeZone = 'UTC'): { from: string; to: string } {
  const offset = zoneOffset(day, timeZone);
  const start = new Date(`${day}T00:00:00${offset}`);
  return { from: start.toISOString(), to: new Date(start.getTime() + 86_400_000).toISOString() };
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** The top-level fields an audit entry changed; every field of a creation or deletion. */
export function changedFields(before: unknown, after: unknown): string[] {
  const a = asRecord(before);
  const b = asRecord(after);
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].filter(
    (key) => key !== 'id' && key !== 'via',
  );
  if (before === null || after === null) return keys;
  return keys.filter((key) => JSON.stringify(a[key]) !== JSON.stringify(b[key]));
}
